import React, { useEffect, useRef, useState } from "react";
import Icon from "../../UI/Icon";
import Modal from "../../UI/Modal";

const isNarrow = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767.98px)").matches;

/**
 * Sort control: popover menu on desktop, bottom sheet on phones.
 * `options`: [{ value, label }]
 */
function SortMenu({ value, options, onChange, compact }) {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const current = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const index = Math.max(0, options.findIndex((o) => o.value === value));
    if (itemRefs.current[index]) itemRefs.current[index].focus();
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open, options, value]);

  const choose = (next) => {
    onChange(next);
    if (open && triggerRef.current) triggerRef.current.focus();
    setOpen(false);
    setSheet(false);
  };

  const onMenuKeyDown = (event) => {
    const items = itemRefs.current.filter(Boolean);
    const index = items.indexOf(document.activeElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(index + 1) % items.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      items[(index - 1 + items.length) % items.length].focus();
    } else if (event.key === "Escape") {
      setOpen(false);
      if (triggerRef.current) triggerRef.current.focus();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div className="v-menu v-sort" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`v-toolbar-btn ${compact ? "v-toolbar-btn--compact" : ""}`}
        aria-haspopup={isNarrow() ? "dialog" : "menu"}
        aria-expanded={open || sheet}
        onClick={() => (isNarrow() ? setSheet(true) : setOpen((v) => !v))}
      >
        <Icon name="sort" size={18} />
        <span className="v-sort__label">
          Sort
          {!compact ? (
            <span className="v-sort__current">
              : <strong>{current.label}</strong>
            </span>
          ) : null}
        </span>
        {!compact ? <Icon name="chevronDown" size={16} /> : null}
      </button>

      {open ? (
        <div className="v-menu__popover" role="menu" aria-label="Sort products" onKeyDown={onMenuKeyDown}>
          {options.map((option, i) => (
            <button
              key={option.value}
              ref={(node) => {
                itemRefs.current[i] = node;
              }}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className="v-menu__item"
              onClick={() => choose(option.value)}
            >
              <span>{option.label}</span>
              {option.value === value ? <Icon name="check" size={16} className="v-menu__check" /> : null}
            </button>
          ))}
        </div>
      ) : null}

      <Modal open={sheet} onClose={() => setSheet(false)} title="Sort by" variant="sheet">
        <div role="radiogroup" aria-label="Sort by" className="v-sort__sheet">
          {options.map((option) => (
            <label key={option.value} className="v-check v-check--row">
              <input
                type="radio"
                name="v-sort"
                checked={option.value === value}
                onChange={() => choose(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}

export default SortMenu;
